import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@repo/shared-styles";
import {staticUrl} from "@repo/urls";

import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";

type Props = WithPrefersReducedMotionProps;

/**
 * JoinPageDecorations renders cute sparkles and a rocket ship!
 *
 * We mostly scale the positions by percentage, but there's a breakpoint at
 * md/lg where we do more manual layout tweaks!
 */
function JoinPageDecorations({
    prefersReducedMotion,
}: Props): React.ReactElement {
    return (
        <>
            <View style={[styles.decorations, styles.lgOrLarger]}>
                <img
                    src={staticUrl("/images/join/rocket.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/rocket.png"),
                        staticUrl("/images/join/rocket@2x.png"),
                    )}
                    alt=""
                    width="592"
                    height="283"
                    style={{position: "absolute", bottom: 0, left: 0}}
                />
                <img
                    src={staticUrl("/images/join/blue-blob.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/blue-blob.png"),
                        staticUrl("/images/join/blue-blob@2x.png"),
                    )}
                    alt=""
                    width="390"
                    height="281"
                    style={{position: "absolute", top: 0, right: 0}}
                />
                <img
                    src={staticUrl("/images/join/orange-blob.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/orange-blob.png"),
                        staticUrl("/images/join/orange-blob@2x.png"),
                    )}
                    alt=""
                    width="196"
                    height="169"
                    style={{
                        position: "absolute",
                        bottom: "5%",
                        right: "20%",
                    }}
                />
                <CircleDecoration
                    style={{
                        position: "absolute",
                        left: "3%",
                        top: "13%",
                    }}
                />
                <SparkleDecoration
                    className={css(!prefersReducedMotion && styles.wobble1)}
                    style={{
                        position: "absolute",
                        left: "14%",
                        top: "10%",
                    }}
                />
                <SparkleDecoration
                    className={css(!prefersReducedMotion && styles.wobble2)}
                    style={{
                        position: "absolute",
                        right: "6%",
                        top: "26%",
                    }}
                />
                <AsteriskDecoration
                    className={css(!prefersReducedMotion && styles.spin2)}
                    style={{
                        position: "absolute",
                        left: "8%",
                        bottom: "33%",
                    }}
                />
                <AsteriskDecoration
                    className={css(!prefersReducedMotion && styles.spin1)}
                    style={{
                        position: "absolute",
                        right: "14%",
                        bottom: "4%",
                    }}
                />
                <SparkleDecoration
                    style={{
                        position: "absolute",
                        left: "40%",
                        bottom: "14%",
                    }}
                />
            </View>
            <View style={[styles.decorations, styles.mdOrSmaller]}>
                <img
                    src={staticUrl("/images/join/rocket.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/rocket.png"),
                        staticUrl("/images/join/rocket@2x.png"),
                    )}
                    alt=""
                    width="310"
                    height="148"
                    style={{position: "absolute", bottom: 0, left: 0}}
                />
                <img
                    src={staticUrl("/images/join/blue-blob.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/blue-blob.png"),
                        staticUrl("/images/join/blue-blob@2x.png"),
                    )}
                    alt=""
                    width="390"
                    height="281"
                    style={{position: "absolute", top: 0, right: -200}}
                />
                <img
                    src={staticUrl("/images/join/orange-blob.png")}
                    srcSet={srcSet(
                        staticUrl("/images/join/orange-blob.png"),
                        staticUrl("/images/join/orange-blob@2x.png"),
                    )}
                    alt=""
                    width="196"
                    height="169"
                    style={{
                        position: "absolute",
                        bottom: "16%",
                        right: "20%",
                    }}
                />
                <CircleDecoration
                    style={{
                        position: "absolute",
                        left: "10%",
                        top: "27%",
                    }}
                />
                <SparkleDecoration
                    className={css(!prefersReducedMotion && styles.wobble1)}
                    style={{
                        position: "absolute",
                        left: "6%",
                        top: "41%",
                    }}
                />
                <SparkleDecoration
                    className={css(!prefersReducedMotion && styles.wobble2)}
                    style={{
                        position: "absolute",
                        right: "6%",
                        top: "26%",
                    }}
                />
                <AsteriskDecoration
                    className={css(!prefersReducedMotion && styles.spin2)}
                    style={{
                        position: "absolute",
                        right: "14%",
                        bottom: "4%",
                    }}
                />
            </View>
        </>
    );
}

function CircleDecoration({style}: {style: any}) {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            style={style}
        >
            <path
                d="M5.70 2.40C2.00 4.28 0.53 8.80 2.41 12.49C4.29 16.18 8.81 17.65 12.51 15.77C16.21 13.89 17.68 9.38 15.80 5.68C13.92 1.99 9.40 0.52 5.70 2.40Z"
                stroke="white"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SparkleDecoration({
    className,
    style,
}: {
    className?: string;
    style: any;
}) {
    return (
        <svg
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            className={className}
            style={style}
        >
            <path
                d="M15.97 1C12.28 5.51 12.85 12.13 17.26 15.95C12.74 12.26 6.10 12.83 2.28 17.23C5.98 12.72 5.41 6.09 1.00 2.28C5.53 5.98 12.15 5.41 15.97 1Z"
                stroke="white"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function AsteriskDecoration({
    className,
    style,
}: {
    className?: string;
    style: any;
}) {
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={className}
            style={style}
        >
            <path
                d="M16.17 10.19L1.41 7.37"
                stroke="#FFB100"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M7.38 16.14L10.21 1.41"
                stroke="#FFB100"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M13.02 14.99L4.58 2.57"
                stroke="#FFB100"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M2.58 12.99L15.02 4.57"
                stroke="#FFB100"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const srcSet = (src1x: string, src2x: string): string =>
    `${src1x} 1x, ${src2x} 2x`;

const styles = StyleSheet.create({
    decorations: {
        backgroundColor: color.darkBlue,

        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
    },

    lgOrLarger: {
        display: "none",

        [mediaQueries.lgOrLarger]: {
            display: "block",
        },
    },

    mdOrSmaller: {
        display: "none",

        [mediaQueries.mdOrSmaller]: {
            display: "block",
        },
    },

    wobble1: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { transform: string; }; "5%": { transform: string; }; "10%": { transform: string; }; "12%": { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                transform: "rotate(0deg)",
            },

            "5%": {
                transform: "rotate(-20deg) scale(0.9)",
            },

            "10%": {
                transform: "rotate(3deg)",
            },

            "12%": {
                transform: "rotate(0deg)",
            },
        },
        animationDuration: "4s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },

    wobble2: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { transform: string; }; "5%": { transform: string; }; "10%": { transform: string; }; "12%": { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                transform: "rotate(0deg)",
            },

            "5%": {
                transform: "rotate(20deg) scale(0.9)",
            },

            "10%": {
                transform: "rotate(-3deg)",
            },

            "12%": {
                transform: "rotate(0deg)",
            },
        },
        animationDelay: "0.4s",
        animationDuration: "4s",
        animationIterationCount: "infinite",
        animationTimingFunction: "ease-in-out",
    },

    spin1: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { transform: string; }; "100%": { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                transform: "rotate(0deg)",
            },

            "100%": {
                transform: "rotate(360deg)",
            },
        },
        animationDuration: "6s",
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
    },

    spin2: {
        // @ts-expect-error - TS2322 - Type '{ "0%": { transform: string; }; "100%": { transform: string; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: {
            "0%": {
                transform: "rotate(0deg)",
            },

            "100%": {
                transform: "rotate(-360deg)",
            },
        },
        animationDuration: "6s",
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
    },
});

type ExportProps = WithoutPrefersReducedMotion<
    JSX.LibraryManagedAttributes<
        typeof JoinPageDecorations,
        React.ComponentProps<typeof JoinPageDecorations>
    >
>;

export default withPrefersReducedMotion(
    JoinPageDecorations,
) as React.ComponentType<ExportProps>;
